/**
 * VoiceNoteRecorder — кнопка-микрофон для записи голосового примечания.
 *
 * Поведение:
 *   idle      → клик: запрос микрофона, переход в recording
 *   recording → клик ❯ stop: останавливает + отправляет на бэк
 *               клик ✕ cancel: отменяет (файл не уходит)
 *               индикатор времени + анимация пульса
 *   uploading → spinner
 *
 * Бэк: POST /api/voice-notes/send multipart (file + amo_id + duration_sec +
 *   category + propagate_to_production).
 */
import { useEffect, useRef, useState } from "react";


type SendArgs = {
  file: Blob;
  durationSec: number;
};

interface Props {
  /** Колбэк когда запись завершена и пользователь хочет отправить. */
  onSend: (args: SendArgs) => Promise<void>;
  /** Отключить кнопку (например пока отправляется другое примечание). */
  disabled?: boolean;
  /** Размер кнопки в px. По умолчанию 34. */
  size?: number;
  /** Цвет акцента (мик/пульсация). По умолчанию #f9a825. */
  accent?: string;
  /** Подсказка-title когда в idle. */
  idleTitle?: string;
}


export function VoiceNoteRecorder({ onSend, disabled, size = 34, accent = "#f9a825", idleTitle }: Props) {
  const [state, setState] = useState<"idle" | "recording" | "uploading">("idle");
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef<{
    mr: MediaRecorder;
    chunks: Blob[];
    stream: MediaStream;
    timer: ReturnType<typeof setInterval> | null;
    startedAt: number;
  } | null>(null);

  useEffect(() => () => {
    // На unmount — гасим всё корректно
    const r = recRef.current;
    if (r) {
      try { if (r.mr.state !== "inactive") r.mr.stop(); } catch { /* */ }
      r.stream.getTracks().forEach(t => t.stop());
      if (r.timer) clearInterval(r.timer);
    }
  }, []);

  async function startRecord() {
    if (state !== "idle") return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      alert(`Нет доступа к микрофону: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    // Подбираем mime для MediaRecorder: предпочитаем webm/opus, на Safari — mp4/aac.
    let mime = "";
    for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
    }
    let mr: MediaRecorder;
    try {
      mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (e) {
      stream.getTracks().forEach(t => t.stop());
      alert(`Не удалось запустить запись: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    const chunks: Blob[] = [];
    mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunks.push(ev.data); };
    const startedAt = Date.now();
    setElapsed(0);
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    recRef.current = { mr, chunks, stream, timer, startedAt };
    mr.start(250);  // dataavailable каждые 250мс
    setState("recording");
  }

  async function stopAndSend() {
    const r = recRef.current;
    if (!r || state !== "recording") return;
    setState("uploading");
    if (r.timer) clearInterval(r.timer);
    // Ждём остановки + последний chunk
    await new Promise<void>(resolve => {
      r.mr.onstop = () => resolve();
      try { r.mr.stop(); } catch { resolve(); }
    });
    r.stream.getTracks().forEach(t => t.stop());
    const blob = new Blob(r.chunks, { type: r.mr.mimeType || "audio/webm" });
    const durationSec = Math.max(1, Math.round((Date.now() - r.startedAt) / 1000));
    recRef.current = null;
    setElapsed(0);
    try {
      await onSend({ file: blob, durationSec });
    } finally {
      setState("idle");
    }
  }

  function cancelRecord() {
    const r = recRef.current;
    if (!r) return;
    if (r.timer) clearInterval(r.timer);
    try { if (r.mr.state !== "inactive") r.mr.stop(); } catch { /* */ }
    r.stream.getTracks().forEach(t => t.stop());
    recRef.current = null;
    setElapsed(0);
    setState("idle");
  }

  if (state === "idle") {
    return (
      <button
        type="button"
        onClick={startRecord}
        disabled={disabled}
        title={idleTitle ?? "Записать голосовое"}
        style={{
          width: size, height: size,
          background: "transparent",
          border: "1px solid var(--line)",
          borderRadius: 6,
          color: "var(--mute)",
          cursor: disabled ? "default" : "pointer",
          fontSize: 16, lineHeight: 1,
          display: "grid", placeItems: "center",
          flexShrink: 0,
          transition: "background 100ms, color 100ms, border-color 100ms",
        }}
        onMouseEnter={(e) => { if (!disabled) { (e.currentTarget as HTMLButtonElement).style.color = accent; (e.currentTarget as HTMLButtonElement).style.borderColor = accent; } }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "var(--mute)"; (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--line)"; }}
      >🎤</button>
    );
  }

  if (state === "uploading") {
    return (
      <div style={{
        height: size, padding: `0 12px`,
        background: "var(--paper-2, #f4f4f5)",
        border: "1px solid var(--line)", borderRadius: 6,
        display: "inline-flex", alignItems: "center", gap: 8,
        fontSize: 12, color: "var(--mute)", flexShrink: 0,
      }}>
        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 5, background: accent, animation: "pulse-dot 1s infinite" }}/>
        Отправляю…
        <style>{`@keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
      </div>
    );
  }

  // recording
  return (
    <div style={{
      height: size, padding: "0 6px 0 10px",
      background: "#fff",
      border: `1px solid ${accent}`, borderRadius: 6,
      display: "inline-flex", alignItems: "center", gap: 8,
      flexShrink: 0,
    }}>
      <span style={{
        display: "inline-block", width: 10, height: 10, borderRadius: 5,
        background: "#e63946",
        animation: "voice-rec-pulse 1.1s ease-in-out infinite",
      }}/>
      <style>{`@keyframes voice-rec-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:0.5} }`}</style>
      <span style={{
        fontSize: 12, color: "var(--ink)", fontVariantNumeric: "tabular-nums",
        minWidth: 28,
      }}>{fmtElapsed(elapsed)}</span>
      <button
        type="button"
        onClick={cancelRecord}
        title="Отменить запись"
        style={{
          width: size - 8, height: size - 8,
          background: "transparent", border: "none",
          color: "var(--mute)", fontSize: 14, cursor: "pointer",
          padding: 0, lineHeight: 1,
        }}
      >✕</button>
      <button
        type="button"
        onClick={stopAndSend}
        title="Отправить"
        style={{
          width: size - 4, height: size - 4,
          background: accent, color: "#fff",
          border: "none", borderRadius: 4,
          fontSize: 14, cursor: "pointer",
          display: "grid", placeItems: "center",
          padding: 0, lineHeight: 1,
        }}
      >➤</button>
    </div>
  );
}


function fmtElapsed(sec: number): string {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}


/**
 * VoiceNotePlayer — компактный плеер для отрисовки уже сохранённого
 * голосового. Принимает строку текста примечания, ищет в ней маркер
 * вида «🎤 ... /api/voice-notes/{name}/audio» и рендерит <audio controls>.
 *
 * Если маркера не нашли — возвращает null (рендеринг делает caller).
 */
export function extractVoiceAudioUrl(text: string): string | null {
  if (!text || !text.includes("🎤")) return null;
  // Ищем URL: «/api/voice-notes/{name}/audio» — относительный или абсолютный
  const m = text.match(/(https?:\/\/[^\s]+)?\/api\/voice-notes\/[a-zA-Z0-9_.\-]+\/audio/);
  return m ? m[0] : null;
}

export function VoiceNotePlayer({ src }: { src: string }) {
  return (
    <audio
      controls
      src={src}
      preload="metadata"
      style={{
        height: 32, display: "block", marginTop: 4,
        width: "100%", maxWidth: 320,
      }}
    />
  );
}
